/**
 * Memory diagnostics utilities for tracking down memory leaks.
 *
 * This is an OPT-IN feature that must be explicitly enabled via localStorage.
 * When enabled, lightweight metrics are automatically sent to Segment every minute.
 *
 * To enable (in browser console):
 *   window.memoryDiagnostics.enable()
 *
 * To disable:
 *   window.memoryDiagnostics.disable()
 *
 * To customize interval (in milliseconds, default 60000):
 *   localStorage.setItem('gitpod-memory-diagnostics-interval', '30000')
 *
 * Available commands:
 *   window.memoryDiagnostics.enable()       - Enable metrics and reload
 *   window.memoryDiagnostics.disable()      - Disable metrics and reload
 *   window.memoryDiagnostics.isEnabled()    - Check if enabled
 *   window.memoryDiagnostics.report()       - Get current memory state (console)
 *   window.memoryDiagnostics.queryCache()   - Detailed React Query cache analysis (console)
 *   window.memoryDiagnostics.startDeepDiagnostics() - Start heavy instrumentation (console only)
 *   window.memoryDiagnostics.stopDeepDiagnostics()  - Stop heavy instrumentation
 */

import type { AnalyticsBrowser } from "@segment/analytics-next";
import type { QueryCache, QueryClient } from "@tanstack/react-query";

const STORAGE_KEY = "gitpod-memory-diagnostics";
const INTERVAL_KEY = "gitpod-memory-diagnostics-interval";
const DEFAULT_INTERVAL_MS = 60000; // 1 minute

// Session start time for calculating duration
const sessionStartTime = Date.now();

// ============================================================================
// Global Stream Tracking (used by useStreamEvents, useStreamConversation, etc.)
// ============================================================================

// Initialize global stream tracking
if (!window.__ACTIVE_STREAMS__) {
    window.__ACTIVE_STREAMS__ = new Set();
}
if (window.__STREAM_RECONNECT_COUNT__ === undefined) {
    window.__STREAM_RECONNECT_COUNT__ = 0;
}

/**
 * Track a stream starting. Call this when opening a new stream connection.
 */
export function trackStreamStart(id: string): void {
    window.__ACTIVE_STREAMS__?.add(id);
    window.__STREAM_RECONNECT_COUNT__ = (window.__STREAM_RECONNECT_COUNT__ ?? 0) + 1;
}

/**
 * Track a stream ending. Call this when a stream connection closes.
 */
export function trackStreamEnd(id: string): void {
    window.__ACTIVE_STREAMS__?.delete(id);
}

/**
 * Check if memory diagnostics is enabled via localStorage
 */
function isEnabled(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
        return false;
    }
}

/**
 * Get the configured interval in milliseconds
 */
function getIntervalMs(): number {
    try {
        const stored = localStorage.getItem(INTERVAL_KEY);
        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed) && parsed >= 10000) {
                // Minimum 10 seconds
                return parsed;
            }
        }
    } catch {
        // Ignore
    }
    return DEFAULT_INTERVAL_MS;
}

/**
 * Enable memory diagnostics (requires page reload)
 */
function enableDiagnostics(): void {
    try {
        localStorage.setItem(STORAGE_KEY, "true");
        console.log("[MemoryDiagnostics] Enabled. Reloading page...");
        window.location.reload();
    } catch (e) {
        console.error("[MemoryDiagnostics] Failed to enable:", e);
    }
}

/**
 * Disable memory diagnostics (requires page reload)
 */
function disableDiagnostics(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(INTERVAL_KEY);
        console.log("[MemoryDiagnostics] Disabled. Reloading page...");
        window.location.reload();
    } catch (e) {
        console.error("[MemoryDiagnostics] Failed to disable:", e);
    }
}

// ============================================================================
// Lightweight Metrics (sent to Segment)
// ============================================================================

type MetricsSnapshot = {
    sessionDurationMinutes: number;
    queryCount: number;
    queryObserverCount: number;
    queryCacheSizeKB: number;
    streamCacheEntries: number;
    streamCacheSizeKB: number;
    activeStreams: number;
    streamReconnectCount: number;
    tabVisible: boolean;
};

type WindowWithSegment = Window & {
    __SEGMENT_ANALYTICS__?: AnalyticsBrowser;
};

type WindowWithQueryClient = Window & {
    __REACT_QUERY_DEVTOOLS__?: {
        queryClient?: QueryClient;
    };
};

type StreamCacheExports = {
    getMemoryCacheSize?: () => number;
    getMemoryCacheBytes?: () => number;
};

type WindowWithStreamCache = Window & {
    __streamCacheExports__?: StreamCacheExports;
};

let metricsIntervalId: number | null = null;

/**
 * Get the Segment analytics instance
 */
function getSegmentAnalytics(): AnalyticsBrowser | null {
    const win = window as WindowWithSegment;
    return win.__SEGMENT_ANALYTICS__ ?? null;
}

/**
 * Get the React Query client
 */
function getQueryClient(): QueryClient | null {
    const win = window as WindowWithQueryClient;
    return win.__REACT_QUERY_DEVTOOLS__?.queryClient ?? null;
}

/**
 * Get stream cache info
 */
function getStreamCacheInfo(): { size: number; entries: number } {
    try {
        const win = window as WindowWithStreamCache;
        const streamCache = win.__streamCacheExports__;
        if (streamCache) {
            return {
                size: streamCache.getMemoryCacheBytes?.() ?? 0,
                entries: streamCache.getMemoryCacheSize?.() ?? 0,
            };
        }
    } catch {
        // Ignore
    }
    return { size: 0, entries: 0 };
}

/**
 * Estimate the size of the query cache in bytes
 */
function estimateQueryCacheSize(queryCache: QueryCache | null): number {
    if (!queryCache) return 0;

    let totalSize = 0;
    for (const query of queryCache.getAll()) {
        try {
            const data = query.state?.data;
            if (data) {
                totalSize += JSON.stringify(data).length;
            }
        } catch {
            totalSize += 1000; // Estimate for circular refs
        }
    }
    return totalSize;
}

/**
 * Count total query observers
 */
function countQueryObservers(queryCache: QueryCache | null): number {
    if (!queryCache) return 0;
    return queryCache.getAll().reduce((sum, q) => sum + q.getObserversCount(), 0);
}

/**
 * Get active stream count from global tracker
 */
function getActiveStreamCount(): number {
    return window.__ACTIVE_STREAMS__?.size ?? 0;
}

/**
 * Get total stream reconnection count for this session
 */
function getStreamReconnectCount(): number {
    return window.__STREAM_RECONNECT_COUNT__ ?? 0;
}

/**
 * Collect current metrics snapshot
 */
function collectMetrics(): MetricsSnapshot {
    const queryClient = getQueryClient();
    const queryCache = queryClient?.getQueryCache() ?? null;
    const streamCacheInfo = getStreamCacheInfo();

    return {
        sessionDurationMinutes: Math.round((Date.now() - sessionStartTime) / 1000 / 60),
        queryCount: queryCache?.getAll()?.length ?? 0,
        queryObserverCount: countQueryObservers(queryCache),
        queryCacheSizeKB: Math.round(estimateQueryCacheSize(queryCache) / 1024),
        streamCacheEntries: streamCacheInfo.entries,
        streamCacheSizeKB: Math.round(streamCacheInfo.size / 1024),
        activeStreams: getActiveStreamCount(),
        streamReconnectCount: getStreamReconnectCount(),
        tabVisible: document.visibilityState === "visible",
    };
}

/**
 * Send metrics to Segment
 */
function sendMetricsToSegment(metrics: MetricsSnapshot): void {
    const analytics = getSegmentAnalytics();
    if (!analytics) {
        console.debug("[MemoryDiagnostics] Segment analytics not available");
        return;
    }

    analytics.track("Dashboard Memory Metrics", metrics).catch((e) => {
        console.debug("[MemoryDiagnostics] Failed to send metrics:", e);
    });
}

/**
 * Start automatic metrics collection and reporting to Segment
 */
function startMetricsCollection(): void {
    if (metricsIntervalId !== null) {
        return; // Already running
    }

    const intervalMs = getIntervalMs();
    console.log(`[MemoryDiagnostics] Starting metrics collection (interval: ${intervalMs / 1000}s)`);

    // Send initial metrics after a short delay (let the page settle)
    setTimeout(() => {
        const metrics = collectMetrics();
        sendMetricsToSegment(metrics);
        console.log("[MemoryDiagnostics] Initial metrics sent:", metrics);
    }, 5000);

    // Send periodic metrics
    metricsIntervalId = window.setInterval(() => {
        const metrics = collectMetrics();
        sendMetricsToSegment(metrics);
        console.debug("[MemoryDiagnostics] Metrics sent:", metrics);
    }, intervalMs);
}

/**
 * Stop automatic metrics collection
 */
function stopMetricsCollection(): void {
    if (metricsIntervalId !== null) {
        clearInterval(metricsIntervalId);
        metricsIntervalId = null;
        console.log("[MemoryDiagnostics] Metrics collection stopped");
    }
}

// ============================================================================
// Deep Diagnostics (console only, heavier instrumentation)
// ============================================================================

type DeepDiagnosticsState = {
    snapshots: MetricsSnapshot[];
    intervalId: number | null;
    initialized: boolean;
    activeTimers: Set<number>;
    activeIntervals: Set<number>;
    // Use WeakMap to avoid preventing garbage collection of removed DOM elements
    eventListeners: WeakMap<EventTarget, Map<string, number>>;
    // Track targets separately for counting (will be approximate for GC'd elements)
    eventListenerTargets: Set<EventTarget>;
    eventListenerCount: number;
};

const deepState: DeepDiagnosticsState = {
    snapshots: [],
    intervalId: null,
    initialized: false,
    activeTimers: new Set(),
    activeIntervals: new Set(),
    eventListeners: new WeakMap(),
    eventListenerTargets: new Set(),
    eventListenerCount: 0,
};

// Store original functions - must be captured at module load time before any instrumentation
const originalSetTimeout = window.setTimeout.bind(window);
const originalSetInterval = window.setInterval.bind(window);
const originalClearTimeout = window.clearTimeout.bind(window);
const originalClearInterval = window.clearInterval.bind(window);

// Capture original prototype methods at module load time to avoid infinite recursion
// after instrumentEventListeners() replaces EventTarget.prototype.addEventListener
// eslint-disable-next-line @typescript-eslint/unbound-method
const originalPrototypeAddEventListener = EventTarget.prototype.addEventListener;
// eslint-disable-next-line @typescript-eslint/unbound-method
const originalPrototypeRemoveEventListener = EventTarget.prototype.removeEventListener;

const prototypeAddEventListener = function (
    this: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
): void {
    return originalPrototypeAddEventListener.call(this, type, listener, options);
};

const prototypeRemoveEventListener = function (
    this: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions,
): void {
    return originalPrototypeRemoveEventListener.call(this, type, listener, options);
};

/**
 * Instrument timers for deep diagnostics
 */
function instrumentTimers(): void {
    if (deepState.initialized) return;

    window.setTimeout = function (callback: TimerHandler, delay?: number, ...args: unknown[]): number {
        const id = originalSetTimeout(callback, delay, ...args);
        deepState.activeTimers.add(id);

        originalSetTimeout(() => {
            deepState.activeTimers.delete(id);
        }, (delay ?? 0) + 100);

        return id;
    } as typeof window.setTimeout;

    window.setInterval = function (callback: TimerHandler, delay?: number, ...args: unknown[]): number {
        const id = originalSetInterval(callback, delay, ...args);
        deepState.activeIntervals.add(id);
        return id;
    } as typeof window.setInterval;

    window.clearTimeout = function (id?: number | string | ReturnType<typeof setTimeout>): void {
        if (typeof id === "number") {
            deepState.activeTimers.delete(id);
        }
        originalClearTimeout(id as number | undefined);
    } as typeof window.clearTimeout;

    window.clearInterval = function (id?: number | string | ReturnType<typeof setInterval>): void {
        if (typeof id === "number") {
            deepState.activeIntervals.delete(id);
        }
        originalClearInterval(id as number | undefined);
    } as typeof window.clearInterval;
}

/**
 * Instrument event listeners for deep diagnostics
 */
function instrumentEventListeners(): void {
    if (deepState.initialized) return;

    EventTarget.prototype.addEventListener = function (
        this: EventTarget,
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | AddEventListenerOptions,
    ): void {
        if (listener) {
            if (!deepState.eventListeners.has(this)) {
                deepState.eventListeners.set(this, new Map());
                deepState.eventListenerTargets.add(this);
            }
            const targetMap = deepState.eventListeners.get(this)!;
            const prevCount = targetMap.get(type) ?? 0;
            targetMap.set(type, prevCount + 1);
            deepState.eventListenerCount++;
        }
        prototypeAddEventListener.call(this, type, listener, options);
    };

    EventTarget.prototype.removeEventListener = function (
        this: EventTarget,
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | EventListenerOptions,
    ): void {
        if (listener) {
            const targetMap = deepState.eventListeners.get(this);
            if (targetMap) {
                const count = targetMap.get(type) ?? 0;
                if (count > 0) {
                    deepState.eventListenerCount--;
                    if (count > 1) {
                        targetMap.set(type, count - 1);
                    } else {
                        targetMap.delete(type);
                    }
                    if (targetMap.size === 0) {
                        deepState.eventListenerTargets.delete(this);
                    }
                }
            }
        }
        prototypeRemoveEventListener.call(this, type, listener, options);
    };
}

/**
 * Restore original functions
 */
function restoreOriginals(): void {
    window.setTimeout = originalSetTimeout;
    window.setInterval = originalSetInterval;
    window.clearTimeout = originalClearTimeout;
    window.clearInterval = originalClearInterval;
    EventTarget.prototype.addEventListener = originalPrototypeAddEventListener;
    EventTarget.prototype.removeEventListener = originalPrototypeRemoveEventListener;
    deepState.initialized = false;
    deepState.eventListeners = new WeakMap();
    deepState.eventListenerTargets.clear();
    deepState.eventListenerCount = 0;
}

/**
 * Count total event listeners.
 * Returns the tracked count (may be slightly off if targets were garbage collected).
 */
function countEventListeners(): number {
    return deepState.eventListenerCount;
}

/**
 * Start deep diagnostics (heavy instrumentation, console only)
 */
export function startDeepDiagnostics(intervalMs = 30000): void {
    if (deepState.intervalId !== null) {
        console.warn("[MemoryDiagnostics] Deep diagnostics already running");
        return;
    }

    console.log("[MemoryDiagnostics] Starting deep diagnostics (timer/listener tracking)...");

    instrumentTimers();
    instrumentEventListeners();
    deepState.initialized = true;

    deepState.intervalId = window.setInterval(() => {
        const metrics = collectMetrics();
        const extended = {
            ...metrics,
            timerCount: deepState.activeTimers.size,
            intervalCount: deepState.activeIntervals.size,
            eventListenerCount: countEventListeners(),
        };

        deepState.snapshots.push(metrics);
        if (deepState.snapshots.length > 100) {
            deepState.snapshots.shift();
        }

        console.log("[MemoryDiagnostics] Deep snapshot:", extended);
    }, intervalMs);

    console.log(`[MemoryDiagnostics] Deep diagnostics started (interval: ${intervalMs / 1000}s)`);
}

/**
 * Stop deep diagnostics
 */
export function stopDeepDiagnostics(): void {
    if (deepState.intervalId !== null) {
        clearInterval(deepState.intervalId);
        deepState.intervalId = null;
    }
    restoreOriginals();
    console.log("[MemoryDiagnostics] Deep diagnostics stopped");
}

// ============================================================================
// Console Reports
// ============================================================================

/**
 * Get a memory report (console)
 */
export function getMemoryReport(): {
    current: MetricsSnapshot & { timerCount?: number; intervalCount?: number; eventListenerCount?: number };
    sessionMinutes: number;
} {
    const metrics = collectMetrics();
    const extended = deepState.initialized
        ? {
              ...metrics,
              timerCount: deepState.activeTimers.size,
              intervalCount: deepState.activeIntervals.size,
              eventListenerCount: countEventListeners(),
          }
        : metrics;

    console.log("[MemoryDiagnostics] Current state:");
    console.table(extended);

    return {
        current: extended,
        sessionMinutes: Math.round((Date.now() - sessionStartTime) / 1000 / 60),
    };
}

type QueryCacheReportEntry = {
    queryKey: string;
    dataSize: number;
    staleTime: number | undefined;
    gcTime: number | undefined;
    refetchInterval: unknown;
    lastUpdated: string;
    fetchStatus: string;
};

/**
 * Get detailed query cache info (console)
 */
export function getQueryCacheReport(): QueryCacheReportEntry[] | null {
    const queryClient = getQueryClient();
    if (!queryClient) {
        console.warn("[MemoryDiagnostics] Query client not found. React Query DevTools may not be enabled.");
        return null;
    }

    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.getAll();

    const report: QueryCacheReportEntry[] = queries.map((query) => {
        let dataSize = 0;
        try {
            dataSize = JSON.stringify(query.state?.data).length;
        } catch {
            dataSize = -1;
        }

        const options = query.options as Record<string, unknown> | undefined;
        const staleTimeRaw: unknown = options?.staleTime;
        const gcTimeRaw: unknown = options?.gcTime;
        const refetchIntervalRaw: unknown = options?.refetchInterval;

        return {
            queryKey: JSON.stringify(query.queryKey).slice(0, 100),
            dataSize,
            staleTime: typeof staleTimeRaw === "number" ? staleTimeRaw : undefined,
            gcTime: typeof gcTimeRaw === "number" ? gcTimeRaw : undefined,
            refetchInterval: refetchIntervalRaw,
            lastUpdated: query.state?.dataUpdatedAt ? new Date(query.state.dataUpdatedAt).toISOString() : "never",
            fetchStatus: query.state?.fetchStatus ?? "unknown",
        };
    });

    report.sort((a, b) => b.dataSize - a.dataSize);

    console.log("[MemoryDiagnostics] Query Cache Report (top 20 by size):");
    console.table(report.slice(0, 20));
    console.log(`[MemoryDiagnostics] Total queries: ${report.length}`);
    console.log(
        `[MemoryDiagnostics] Total cache size: ${(report.reduce((sum, q) => sum + Math.max(0, q.dataSize), 0) / 1024).toFixed(2)} KB`,
    );

    return report;
}

// ============================================================================
// Window API and Auto-start
// ============================================================================

declare global {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface Window {
        __SEGMENT_ANALYTICS__?: AnalyticsBrowser;
        __ACTIVE_STREAMS__?: Set<string>;
        __STREAM_RECONNECT_COUNT__?: number;
        memoryDiagnostics: {
            enable: typeof enableDiagnostics;
            disable: typeof disableDiagnostics;
            isEnabled: typeof isEnabled;
            report: typeof getMemoryReport;
            queryCache: typeof getQueryCacheReport;
            startDeepDiagnostics: typeof startDeepDiagnostics;
            stopDeepDiagnostics: typeof stopDeepDiagnostics;
            stopMetrics: typeof stopMetricsCollection;
        };
    }
}

window.memoryDiagnostics = {
    enable: enableDiagnostics,
    disable: disableDiagnostics,
    isEnabled,
    report: getMemoryReport,
    queryCache: getQueryCacheReport,
    startDeepDiagnostics,
    stopDeepDiagnostics,
    stopMetrics: stopMetricsCollection,
};

// Auto-start metrics collection if enabled
if (isEnabled()) {
    console.log("[MemoryDiagnostics] Enabled - metrics will be sent to Segment");
    // Delay start to let the page initialize
    setTimeout(() => {
        startMetricsCollection();
    }, 3000);
}
